<script setup lang="ts">
import { computed } from "vue";
import { UiCard, UiText, UiIcon } from "@shopexperts/shared";
import type { ExpertLevel } from "@shopexperts/legacy-api";
import {
  formatCommission,
  formatThreshold,
} from "@/app/composables/expert-levels/expertLevelForm";

const props = defineProps<{
  level: ExpertLevel;
}>();

const emit = defineEmits<{
  (e: "edit"): void;
  (e: "delete"): void;
}>();

const commissionLabel = computed(() => formatCommission(props.level.commission_rate_bps));
const thresholdLabel = computed(() => formatThreshold(props.level.auto_enroll_threshold_gross_cents));
const description = computed(() => (props.level.public_description ?? "").trim());
const internalNotes = computed(() => (props.level.internal_notes ?? "").trim());
const hasNotes = computed(() => Boolean(internalNotes.value));

function onEdit() {
  emit("edit");
}

function onDelete() {
  emit("delete");
}
</script>

<template>
  <UiCard padding="md" class="hover:shadow-md transition-shadow">
    <div class="flex flex-col gap-0">
      <!-- Main row -->
      <div class="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-6">
        <!-- Level identity -->
        <div class="flex-1 min-w-0 flex flex-col gap-2">
          <div class="flex items-center gap-2 flex-wrap">
            <span
              class="inline-flex items-center justify-center w-7 h-7 rounded-full bg-se-primary/20 text-se-dark text-xs font-semibold font-mono"
              :title="`Sort order ${level.sort_order}`"
              aria-hidden="true"
            >
              {{ level.sort_order }}
            </span>

            <UiText as="p" variant="h6" class="font-semibold text-se-dark truncate">
              {{ level.public_title }}
            </UiText>

            <span
              v-if="level.is_active"
              class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800"
            >
              <span class="w-1.5 h-1.5 rounded-full bg-green-600" aria-hidden="true" />
              Active
            </span>
            <span
              v-else
              class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-se-gray-200 text-se-gray-700"
            >
              <span class="w-1.5 h-1.5 rounded-full bg-se-gray-500" aria-hidden="true" />
              Inactive
            </span>
          </div>

          <UiText
            v-if="description"
            as="p"
            variant="p-small"
            class="text-se-gray-600 line-clamp-2"
            :text="description"
          />
          <UiText
            v-else
            as="p"
            variant="p-small"
            class="text-se-gray-400 italic"
            text="No public description"
          />
        </div>

        <!-- Commission -->
        <div class="shrink-0 min-w-[120px]">
          <UiText as="p" variant="caption-normal" class="text-se-gray-500 uppercase tracking-wider text-xs">
            Commission
          </UiText>
          <UiText as="p" variant="h5" class="font-semibold text-se-dark">
            {{ commissionLabel }}
          </UiText>
        </div>

        <!-- Threshold -->
        <div class="shrink-0 min-w-[180px]">
          <UiText as="p" variant="caption-normal" class="text-se-gray-500 uppercase tracking-wider text-xs">
            Auto-enroll at
          </UiText>
          <div class="flex items-baseline gap-1">
            <UiText as="p" variant="h6" class="font-semibold text-se-dark">
              {{ thresholdLabel }}
            </UiText>
            <UiText as="span" variant="p-small" class="text-se-gray-500">
              gross sales
            </UiText>
          </div>
        </div>

        <!-- Actions -->
        <div class="flex items-center gap-4 shrink-0">
          <button
            type="button"
            class="flex items-center gap-2 text-se-dark hover:text-se-gray-700 transition-colors"
            @click="onEdit"
          >
            <UiIcon name="pencil" class="w-5 h-5" />
            <span class="text-sm font-semibold underline">Edit</span>
          </button>

          <button
            type="button"
            class="flex items-center gap-2 text-se-dark hover:text-red-600 transition-colors"
            @click="onDelete"
          >
            <UiIcon name="delete" class="w-5 h-5" />
            <span class="text-sm font-semibold underline">Delete</span>
          </button>
        </div>
      </div>

      <!-- Admin margin note: editorial annotation, not a banner.
           The brand-accent bar and typographic eyebrow announce the
           register shift from public copy to admin commentary. -->
      <div
        v-if="hasNotes"
        class="mt-4 pt-3 border-t border-dashed border-se-gray-200"
      >
        <div class="flex items-start gap-3 pl-3 border-l-2 border-se-primary">
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2">
              <UiText
                as="span"
                variant="caption-normal"
                class="text-se-primary font-semibold uppercase tracking-[0.18em] text-[10px]"
                text="Admin"
              />
              <span class="h-px w-3 bg-se-gray-300" aria-hidden="true" />
              <UiText
                as="span"
                variant="caption-normal"
                class="text-se-gray-500 uppercase tracking-[0.18em] text-[10px]"
                text="Notes"
              />
            </div>
            <p
              class="mt-1 text-sm italic text-se-gray-700 whitespace-pre-line line-clamp-2"
              :title="internalNotes"
            >
              {{ internalNotes }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </UiCard>
</template>
